import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavStep } from '../../lib/NavHistory';
import {
  Brain, Search, Plus, Filter, ChevronRight, ChevronDown, Activity, Heart, Zap, Shield, Star,
  TrendingUp, AlertTriangle, CheckCircle, XCircle, ArrowLeft, Loader, Send, BookOpen, Layers,
  Target, Sparkles, FileText, Clock, BarChart3, X, Eye, Edit2, Trash2, RefreshCw, Columns3, LayoutList,
  Globe, MapPin, Copy, Check,
} from 'lucide-react';
import {
  safeAsync, Badge, KPI, neuShadow, neuShadowSm, neuShadowInset,
  inp, btnPrimary, btnSm, btnDanger, cardWrap, emptyMsg,
  overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn,
} from './crmShared';

const api = () => window.zignagenixAPI;

// ─── Status & Therapy Type Colors ──────────────────────────────────────────
const THERAPY_COLORS = {
  immunotherapy: '#FF6B6B',
  cell_therapy: '#4ECDC4',
  combination: '#45B7D1',
  pharmaceutical: '#F7DC6F',
  regenerative: '#98D8C8',
  gene_therapy: '#FFA07A',
};

const STATUS_COLORS = {
  draft: '#6C757D',
  under_review: '#F26522',
  approved: '#2E8B98',
  published: '#62BB46',
  archived: '#ADB5BD',
};

const CELL_TYPES = ['IL-15', 'CAR-T', 'NK-T', 'Stem Cell', 'Dendritic', 'Mesenchymal', 'Hematopoietic', 'NK Cell'];

// ─── Main Component ───────────────────────────────────────────────────────
export default function MedicalIntelligenceTab({ onContactClick }) {
  const [tab, setTab] = useState('dashboard'); // dashboard, library, suggestions, categories, detail
  const [protocols, setProtocols] = useState([]);
  const [summary, setSummary] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Protocol Library filters
  const [protocolSearch, setProtocolSearch] = useState('');
  const [therapyTypeFilter, setTherapyTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cellTypesFilter, setCellTypesFilter] = useState([]);

  // Suggestion form
  const [suggestionCondition, setSuggestionCondition] = useState('');
  const [suggestionDiagnosisCodes, setSuggestionDiagnosisCodes] = useState('');
  const [suggestionTherapyType, setSuggestionTherapyType] = useState('');
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestionResults, setSuggestionResults] = useState(null);

  // AI Research Search
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchResults, setAiSearchResults] = useState(null);

  // Document Translation
  const [translateInput, setTranslateInput] = useState('');
  const [translateDocTitle, setTranslateDocTitle] = useState('');
  const [translateDocType, setTranslateDocType] = useState('protocol');
  const [translateSourceLang, setTranslateSourceLang] = useState('');
  const [translateSourceCountry, setTranslateSourceCountry] = useState('');
  const [translateDetecting, setTranslateDetecting] = useState(false);
  const [translateDetected, setTranslateDetected] = useState(null);
  const [translateLoading, setTranslateLoading] = useState(false);
  const [translateResult, setTranslateResult] = useState(null);
  const [translateHistory, setTranslateHistory] = useState([]);
  const [translateCopied, setTranslateCopied] = useState(false);
  const [researchCenters, setResearchCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState('');
  const [supportedLanguages, setSupportedLanguages] = useState([]);

  // Modals
  const [showNewProtocolModal, setShowNewProtocolModal] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // #65 Phase C2b-2: the protocol detail (ProtocolDetail, read-only) is this
  // tab's master->detail sub-view. Register ONE Back step so global Back returns
  // to the Protocol Library LIST before navigating away — same handler the
  // in-view "Back to Library" button uses. Unconditional (this component has no
  // early returns). Label is a LITERAL static string — never a protocol name or
  // id (sessionStorage-backed nav stack). Opening a protocol from the AI-search
  // tab jumps into library detail; Back returns to the library list.
  useNavStep({
    active: tab === 'library' && !!selectedProtocol,
    label: 'Protocol',
    onBack: () => setSelectedProtocol(null),
  });

  // Load summary & recent data
  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [sum, recentAnalyses, recentSuggestions, cats] = await Promise.all([
        api()?.getMISummary?.().catch(() => null),
        api()?.getMIAnalysisHistory?.({}).catch(() => []),
        api()?.getMISuggestions?.({}).catch(() => []),
        api()?.getMICategories?.().catch(() => []),
      ]);
      if (sum) setSummary(sum);
      if (recentAnalyses) setAnalyses(Array.isArray(recentAnalyses) ? recentAnalyses.slice(0, 5) : []);
      if (recentSuggestions) setSuggestions(Array.isArray(recentSuggestions) ? recentSuggestions.slice(0, 5) : []);
      if (cats) setCategories(Array.isArray(cats) ? cats : []);
    } catch (err) {
      console.error('[MI] Dashboard load error:', err);
      setError('Failed to load dashboard');
    }
    setLoading(false);
  }, []);

  const loadProtocols = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filters = { search: protocolSearch };
      if (therapyTypeFilter) filters.therapy_type = therapyTypeFilter;
      if (categoryFilter) filters.category = categoryFilter;
      if (statusFilter) filters.status = statusFilter;
      if (cellTypesFilter.length > 0) filters.cell_types = cellTypesFilter;
      const data = await api()?.getMIProtocols?.(filters).catch(() => []);
      setProtocols(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[MI] Protocol load error:', err);
      setError('Failed to load protocols');
    }
    setLoading(false);
  }, [protocolSearch, therapyTypeFilter, categoryFilter, statusFilter, cellTypesFilter]);

  useEffect(() => {
    if (tab === 'library') loadProtocols();
  }, [tab, loadProtocols]);

  const handleCreateProtocol = async (data) => {
    try {
      await api()?.createMIProtocol?.(data);
      setShowNewProtocolModal(false);
      loadProtocols();
    } catch (err) {
      setError('Failed to create protocol: ' + err.message);
    }
  };

  const handleRequestAISuggestion = async () => {
    if (!suggestionCondition.trim()) {
      setError('Please describe a patient condition');
      return;
    }
    setSuggestionLoading(true);
    setError('');
    try {
      const result = await api()?.requestMISuggestion?.({
        condition: suggestionCondition,
        diagnosisCodes: suggestionDiagnosisCodes,
        preferences: { therapy_type: suggestionTherapyType || undefined },
      });
      setSuggestionResults(result);
    } catch (err) {
      setError('Failed to get AI suggestions: ' + err.message);
    }
    setSuggestionLoading(false);
  };

  const toggleCellType = (type) => {
    if (cellTypesFilter.includes(type)) {
      setCellTypesFilter(cellTypesFilter.filter(t => t !== type));
    } else {
      setCellTypesFilter([...cellTypesFilter, type]);
    }
  };

  // ─── AI Research Search Handler ────────────────────────────────────────
  const handleAISearch = useCallback(async () => {
    if (!aiSearchQuery.trim()) { setError('Enter a research query'); return; }
    setAiSearchLoading(true);
    setError('');
    setAiSearchResults(null);
    try {
      const result = await api()?.advancedMISearch?.({ query: aiSearchQuery.trim() });
      setAiSearchResults(result || { results: [], aiInterpretation: null });
    } catch (err) {
      console.error('[MI] AI search error:', err);
      setError('AI search failed: ' + (err.message || 'Unknown error'));
    }
    setAiSearchLoading(false);
  }, [aiSearchQuery]);

  // ─── Translation Handlers ───────────────────────────────────────────
  const loadTranslationData = useCallback(async () => {
    try {
      const [centers, langs, history] = await Promise.all([
        api()?.getResearchCenters?.({}).catch(() => []),
        api()?.getSupportedLanguages?.().catch(() => []),
        api()?.getTranslationHistory?.({}).catch(() => []),
      ]);
      if (centers) setResearchCenters(Array.isArray(centers) ? centers : []);
      if (langs) setSupportedLanguages(Array.isArray(langs) ? langs : []);
      if (history) setTranslateHistory(Array.isArray(history) ? history : []);
    } catch (err) { console.error('[MI] Translation data load error:', err); }
  }, []);

  useEffect(() => {
    if (tab === 'translation') loadTranslationData();
  }, [tab, loadTranslationData]);

  const handleDetectLanguage = useCallback(async () => {
    if (!translateInput.trim()) return;
    setTranslateDetecting(true);
    try {
      const detected = await api()?.detectLanguage?.(translateInput.trim());
      if (detected) {
        setTranslateDetected(detected);
        setTranslateSourceLang(detected.language_code);
        if (detected.country_of_origin) setTranslateSourceCountry(detected.country_of_origin);
      }
    } catch (err) { setError('Language detection failed'); }
    setTranslateDetecting(false);
  }, [translateInput]);

  const handleTranslate = useCallback(async () => {
    if (!translateInput.trim()) { setError('Paste or type a medical document to translate'); return; }
    if (!translateSourceLang) { setError('Please detect or select the source language first'); return; }
    setTranslateLoading(true);
    setError('');
    setTranslateResult(null);
    try {
      const center = researchCenters.find(c => String(c.id) === String(selectedCenter));
      const result = await api()?.translateDocument?.({
        text: translateInput.trim(),
        sourceLanguage: translateSourceLang,
        sourceLangName: translateDetected?.language_name || translateSourceLang,
        targetLanguage: 'en',
        documentType: translateDocType,
        documentTitle: translateDocTitle || undefined,
        sourceCenterId: selectedCenter || undefined,
        sourceCountry: translateSourceCountry || center?.country || undefined,
      });
      setTranslateResult(result);
      loadTranslationData(); // refresh history
    } catch (err) {
      setError('Translation failed: ' + (err.message || 'Unknown error'));
    }
    setTranslateLoading(false);
  }, [translateInput, translateSourceLang, translateDetected, translateDocType, translateDocTitle, selectedCenter, translateSourceCountry, researchCenters, loadTranslationData]);

  const handleCopyTranslation = useCallback(() => {
    if (translateResult?.translated_text) {
      navigator.clipboard?.writeText(translateResult.translated_text);
      setTranslateCopied(true);
      setTimeout(() => setTranslateCopied(false), 2000);
    }
  }, [translateResult]);

  const handleCenterSelect = useCallback((centerId) => {
    setSelectedCenter(centerId);
    const center = researchCenters.find(c => String(c.id) === String(centerId));
    if (center) {
      setTranslateSourceLang(center.primary_language);
      setTranslateSourceCountry(center.country);
      setTranslateDetected({
        language_code: center.primary_language,
        language_name: supportedLanguages.find(l => l.code === center.primary_language)?.name || center.primary_language,
        country_of_origin: center.country,
        confidence: 100,
      });
    }
  }, [researchCenters, supportedLanguages]);

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '20px' }}>
      {error && (
        <div style={{
          background: '#FEE2E2', color: '#991B1B', padding: '12px 14px',
          borderRadius: 10, marginBottom: '16px', fontSize: 13, display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          boxShadow: neuShadowSm,
        }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={16} color="#991B1B" />
          </button>
        </div>
      )}

      {/* ═══ TABS ═══ */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', borderBottom: '2px solid #E5E7EB' }}>
        {[
          { id: 'dashboard', label: 'Dashboard', icon: Brain },
          { id: 'ai_search', label: 'AI Research Search', icon: Search },
          { id: 'translation', label: 'Document Translation', icon: Globe },
          { id: 'library', label: 'Protocol Library', icon: BookOpen },
          { id: 'suggestions', label: 'AI Suggestions', icon: Sparkles },
          { id: 'categories', label: 'Categories', icon: Layers },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '10px 16px', border: 'none',
                background: tab === t.id ? '#2E8B98' : 'transparent',
                color: tab === t.id ? '#fff' : '#6C757D',
                cursor: 'pointer', fontWeight: tab === t.id ? 'bold' : 'normal',
                borderRadius: '4px 4px 0 0', display: 'flex', alignItems: 'center', gap: 6,
              }}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ═══ DASHBOARD VIEW ═══ */}
      {tab === 'dashboard' && (
        <div>
          {/* KPI Row */}
          {summary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
              <KPI icon={Layers} label="Total Protocols" value={summary.total_protocols || 0} color="#2E8B98" />
              <KPI icon={Brain} label="AI Analyses Run" value={summary.total_analyses || 0} color="#F26522" />
              <KPI icon={Sparkles} label="Suggestions Generated" value={summary.total_suggestions || 0} color="#62BB46" />
              <KPI icon={Heart} label="Active Patient Protocols" value={summary.active_patient_protocols || 0} color="#FF6B6B" />
              <KPI icon={Star} label="Avg Efficacy Score" value={summary.avg_efficacy_score?.toFixed(1) || 'N/A'} color="#4ECDC4" />
            </div>
          )}

          {/* Protocol Distribution */}
          <div style={{ ...cardWrap, padding: '16px', marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 700 }}>Protocol Distribution by Category</h3>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {categories.slice(0, 8).map(cat => (
                <div key={cat.id} style={{ flex: '1 1 120px', minWidth: 100 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>{cat.name}</div>
                  <div style={{
                    background: '#E5E7EB', borderRadius: 6, overflow: 'hidden', height: 24,
                  }}>
                    <div style={{
                      background: '#2E8B98', height: '100%', width: `${Math.min((cat.protocol_count / (summary?.total_protocols || 1)) * 100, 100)}%`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 600,
                    }}>
                      {cat.protocol_count || 0}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent AI Analyses */}
          <div style={{ ...cardWrap, padding: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Recent AI Analyses</h3>
              <button onClick={() => setTab('library')} style={{ ...btnSm }}>
                View All <ChevronRight size={12} />
              </button>
            </div>
            {analyses.length > 0 ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {analyses.map(a => (
                  <AnalysisCard key={a.id} analysis={a} />
                ))}
              </div>
            ) : (
              <div style={{ ...emptyMsg, padding: 20 }}>No analyses yet</div>
            )}
          </div>

          {/* Recent AI Suggestions */}
          <div style={{ ...cardWrap, padding: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Recent AI Suggestions</h3>
              <button onClick={() => setTab('suggestions')} style={{ ...btnSm }}>
                View All <ChevronRight size={12} />
              </button>
            </div>
            {suggestions.length > 0 ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {suggestions.map(s => (
                  <SuggestionCard key={s.id} suggestion={s} />
                ))}
              </div>
            ) : (
              <div style={{ ...emptyMsg, padding: 20 }}>No suggestions yet</div>
            )}
          </div>

          {/* Quick Actions */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={() => setShowNewProtocolModal(true)} style={btnPrimary}>
              <Plus size={14} /> New Protocol
            </button>
            <button onClick={() => setTab('suggestions')} style={btnPrimary}>
              <Sparkles size={14} /> AI Suggest Protocol
            </button>
            <button onClick={() => setTab('library')} style={btnPrimary}>
              <Search size={14} /> Search Library
            </button>
          </div>
        </div>
      )}

      {/* ═══ AI RESEARCH SEARCH VIEW ═══ */}
      {tab === 'ai_search' && (
        <div>
          {/* Search Bar */}
          <div style={{ ...cardWrap, padding: '20px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Brain size={18} color="#2E8B98" />
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#212529' }}>AI-Powered Research Search</h3>
            </div>
            <p style={{ margin: '0 0 14px 0', fontSize: 12, color: '#6C757D' }}>
              Ask a natural-language question about protocols, treatments, cell therapies, or conditions.
              The AI will interpret your query, expand medical terminology, and search across the global protocol library.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={aiSearchQuery}
                onChange={e => setAiSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !aiSearchLoading && handleAISearch()}
                placeholder="e.g. NK-T cell protocols for refractory lymphoma with IL-15 combination..."
                style={{ ...inp, flex: 1, fontSize: 13, padding: '12px 14px' }}
              />
              <button
                onClick={handleAISearch}
                disabled={aiSearchLoading}
                style={{ ...btnPrimary, minWidth: 140, display: 'flex', alignItems: 'center', gap: 6 }}>
                {aiSearchLoading ? (
                  <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Searching...</>
                ) : (
                  <><Search size={14} /> AI Search</>
                )}
              </button>
            </div>
          </div>

          {/* AI Interpretation Panel */}
          {aiSearchResults?.aiInterpretation && (
            <div style={{ ...cardWrap, padding: '16px', marginBottom: '16px', background: '#F0FBFF', border: '1px solid #7DD3FC' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <Sparkles size={14} color="#2E8B98" />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0C4A6E' }}>AI Query Interpretation</span>
              </div>
              {aiSearchResults.aiInterpretation.expanded_terms?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#6C757D' }}>Expanded Terms: </span>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                    {aiSearchResults.aiInterpretation.expanded_terms.map((t, i) => (
                      <span key={i} style={{
                        padding: '3px 10px', borderRadius: 12, background: '#E0F2FE',
                        fontSize: 11, color: '#0C4A6E', fontWeight: 500,
                      }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {aiSearchResults.aiInterpretation.related_conditions?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#6C757D' }}>Related Conditions: </span>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                    {aiSearchResults.aiInterpretation.related_conditions.map((c, i) => (
                      <span key={i} style={{
                        padding: '3px 10px', borderRadius: 12, background: '#FEF3C7',
                        fontSize: 11, color: '#92400E', fontWeight: 500,
                      }}>{c}</span>
                    ))}
                  </div>
                </div>
              )}
              {aiSearchResults.aiInterpretation.suggested_therapy_types?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#6C757D' }}>Therapy Types: </span>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                    {aiSearchResults.aiInterpretation.suggested_therapy_types.map((t, i) => (
                      <span key={i} style={{
                        padding: '3px 10px', borderRadius: 12,
                        background: THERAPY_COLORS[t] ? `${THERAPY_COLORS[t]}20` : '#F3E8FF',
                        fontSize: 11, color: THERAPY_COLORS[t] || '#6B21A8', fontWeight: 500,
                        border: `1px solid ${THERAPY_COLORS[t] || '#C084FC'}`,
                      }}>{t.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                </div>
              )}
              {aiSearchResults.aiInterpretation.suggested_cell_types?.length > 0 && (
                <div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#6C757D' }}>Cell Types: </span>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                    {aiSearchResults.aiInterpretation.suggested_cell_types.map((ct, i) => (
                      <span key={i} style={{
                        padding: '3px 10px', borderRadius: 12, background: '#D1FAE5',
                        fontSize: 11, color: '#065F46', fontWeight: 500,
                      }}>{ct}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Search Strategy Breakdown */}
          {aiSearchResults?.searchStrategies && (
            <div style={{ ...cardWrap, padding: '14px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <BarChart3 size={14} color="#6C757D" />
                <span style={{ fontSize: 12, fontWeight: 600, color: '#6C757D' }}>Search Strategies</span>
                {aiSearchResults.processingTimeMs && (
                  <span style={{ fontSize: 10, color: '#ADB5BD', marginLeft: 'auto' }}>
                    <Clock size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                    {aiSearchResults.processingTimeMs}ms
                  </span>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                {[
                  { key: 'directMatch', label: 'Direct Match', icon: Target, color: '#2E8B98' },
                  { key: 'expandedTerms', label: 'Expanded Terms', icon: Zap, color: '#F26522' },
                  { key: 'categoryMatch', label: 'Category Match', icon: Layers, color: '#62BB46' },
                  { key: 'conditionBased', label: 'Condition Based', icon: Heart, color: '#FF6B6B' },
                ].map(s => {
                  const count = aiSearchResults.searchStrategies[s.key] || 0;
                  const SIcon = s.icon;
                  return (
                    <div key={s.key} style={{
                      background: '#F9FAFB', padding: '10px 12px', borderRadius: 10,
                      borderLeft: `3px solid ${s.color}`, textAlign: 'center',
                    }}>
                      <SIcon size={14} color={s.color} style={{ marginBottom: 4 }} />
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#212529' }}>{count}</div>
                      <div style={{ fontSize: 10, color: '#6C757D', fontWeight: 500 }}>{s.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI Research Insights */}
          {aiSearchResults?.aiResearchInsights && (
            <div style={{
              ...cardWrap, padding: '16px', marginBottom: '16px',
              background: 'linear-gradient(135deg, #F0FDFA 0%, #ECFDF5 100%)',
              border: '1px solid #6EE7B7',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <TrendingUp size={14} color="#059669" />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#065F46' }}>AI Research Insights</span>
              </div>
              <p style={{ margin: 0, fontSize: 12, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {aiSearchResults.aiResearchInsights}
              </p>
            </div>
          )}

          {/* Search Results */}
          {aiSearchResults && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#212529' }}>
                  Results ({(aiSearchResults.results || []).length})
                </h3>
                {aiSearchResults.isAIPowered && (
                  <Badge text="AI-Ranked" color="#2E8B98" />
                )}
              </div>

              {(aiSearchResults.results || []).length > 0 ? (
                <div style={{ display: 'grid', gap: 10 }}>
                  {aiSearchResults.results.map((protocol, idx) => {
                    const therapyColor = THERAPY_COLORS[protocol.therapy_type] || '#6C757D';
                    const statusColor = STATUS_COLORS[protocol.status] || '#6C757D';
                    return (
                      <div
                        key={protocol.id || idx}
                        onClick={() => { setSelectedProtocol(protocol); setTab('library'); }}
                        style={{
                          ...cardWrap, padding: '16px', cursor: 'pointer',
                          borderLeft: `4px solid ${therapyColor}`,
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}>
                        {/* Result Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                              <span style={{ fontSize: 11, color: '#ADB5BD', fontWeight: 600 }}>#{idx + 1}</span>
                              <span style={{ fontSize: 11, fontWeight: 600, color: '#6C757D' }}>{protocol.protocol_code}</span>
                              <Badge text={protocol.status} color={statusColor} />
                            </div>
                            <h4 style={{ margin: '0 0 4px 0', fontSize: 14, fontWeight: 700, color: '#212529' }}>
                              {protocol.title}
                            </h4>
                            <p style={{ margin: 0, fontSize: 12, color: '#6C757D' }}>{protocol.condition_treated}</p>
                          </div>
                          {protocol.efficacy_score && (
                            <div style={{ textAlign: 'center', minWidth: 50 }}>
                              <div style={{ fontSize: 18, fontWeight: 700, color: '#62BB46' }}>
                                {protocol.efficacy_score}
                              </div>
                              <div style={{ fontSize: 9, color: '#6C757D', fontWeight: 600 }}>EFFICACY</div>
                            </div>
                          )}
                        </div>

                        {/* Badges */}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                          <Badge text={protocol.therapy_type?.replace(/_/g, ' ')} color={therapyColor} />
                          {protocol.cell_types && protocol.cell_types.slice(0, 3).map(ct => (
                            <Badge key={ct} text={ct} color="#4ECDC4" />
                          ))}
                        </div>

                        {/* AI Relevance Note */}
                        {protocol.ai_relevance_note && (
                          <div style={{
                            background: '#F0FBFF', padding: '8px 12px', borderRadius: 8,
                            fontSize: 11, color: '#0C4A6E', lineHeight: 1.5,
                            borderLeft: '3px solid #2E8B98',
                          }}>
                            <Sparkles size={10} style={{ verticalAlign: 'middle', marginRight: 4, color: '#2E8B98' }} />
                            {protocol.ai_relevance_note}
                          </div>
                        )}

                        {/* View Details */}
                        <div style={{ display: 'flex', alignItems: 'center', color: '#2E8B98', fontSize: 12, fontWeight: 600, marginTop: 8 }}>
                          View Protocol <ChevronRight size={12} style={{ marginLeft: 4 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ ...cardWrap, padding: '40px 20px', textAlign: 'center' }}>
                  <Search size={32} color="#ADB5BD" style={{ marginBottom: 12 }} />
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>No Matching Protocols</div>
                  <p style={{ margin: 0, fontSize: 12, color: '#ADB5BD' }}>
                    Try broadening your search query or using different medical terminology.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Initial Empty State */}
          {!aiSearchResults && !aiSearchLoading && (
            <div style={{ ...cardWrap, padding: '48px 20px', textAlign: 'center' }}>
              <Brain size={48} color="#2E8B98" style={{ marginBottom: 16, opacity: 0.5 }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: '#212529', marginBottom: 8 }}>
                Global Protocol Intelligence
              </div>
              <p style={{ margin: '0 auto', fontSize: 12, color: '#6C757D', maxWidth: 480, lineHeight: 1.6 }}>
                Search across the global medical protocol library using natural language.
                The AI engine interprets your query, expands medical terminology, cross-references
                cell therapies, conditions, and combination protocols to find the most relevant results.
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20, flexWrap: 'wrap' }}>
                {['IL-15 + CAR-T combination therapy', 'Stem cell protocols for autoimmune', 'NK-T cell immunotherapy refractory'].map(q => (
                  <button
                    key={q}
                    onClick={() => { setAiSearchQuery(q); }}
                    style={{
                      padding: '8px 14px', borderRadius: 20, border: '1px solid #E5E7EB',
                      background: '#F9FAFB', cursor: 'pointer', fontSize: 11, color: '#374151',
                      boxShadow: neuShadowSm,
                    }}>
                    <Search size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ DOCUMENT TRANSLATION VIEW ═══ */}
      {tab === 'translation' && (
        <div>
          {/* Header */}
          <div style={{ ...cardWrap, padding: '20px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Globe size={18} color="#2E8B98" />
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#212529' }}>Medical Document Translation</h3>
              <Badge text={`${researchCenters.length} Centers`} color="#62BB46" />
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#6C757D' }}>
              Translate medical research documents from any language into English while preserving exact formatting,
              medical terminology, and document structure. Each translation records the originating country and research center.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: translateResult ? '1fr 1fr' : '1fr', gap: 16 }}>
            {/* Source Panel */}
            <div>
              {/* Research Center & Metadata */}
              <div style={{ ...cardWrap, padding: '14px', marginBottom: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>Research Center</label>
                    <select
                      value={selectedCenter}
                      onChange={e => handleCenterSelect(e.target.value)}
                      style={{ ...inp, fontSize: 12 }}>
                      <option value="">Select center...</option>
                      {researchCenters.map(c => (
                        <option key={c.id} value={c.id}>{c.name} — {c.country}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>Document Type</label>
                    <select
                      value={translateDocType}
                      onChange={e => setTranslateDocType(e.target.value)}
                      style={{ ...inp, fontSize: 12 }}>
                      {['protocol','research_paper','clinical_report','lab_results','patient_summary','consent_form'].map(t => (
                        <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>Document Title</label>
                    <input
                      value={translateDocTitle}
                      onChange={e => setTranslateDocTitle(e.target.value)}
                      placeholder="Document title..."
                      style={{ ...inp, fontSize: 12 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>Country of Research</label>
                    <input
                      value={translateSourceCountry}
                      onChange={e => setTranslateSourceCountry(e.target.value)}
                      placeholder="e.g. Japan, Germany..."
                      style={{ ...inp, fontSize: 12 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>Source Language</label>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <select
                        value={translateSourceLang}
                        onChange={e => setTranslateSourceLang(e.target.value)}
                        style={{ ...inp, fontSize: 12, flex: 1 }}>
                        <option value="">Auto-detect...</option>
                        {supportedLanguages.map(l => (
                          <option key={l.code} value={l.code}>{l.name} ({l.native_name})</option>
                        ))}
                      </select>
                      <button
                        onClick={handleDetectLanguage}
                        disabled={translateDetecting || !translateInput.trim()}
                        title="Auto-detect language"
                        style={{ ...btnSm, padding: '6px 10px' }}>
                        {translateDetecting ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={12} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Detection result */}
                {translateDetected && (
                  <div style={{
                    marginTop: 10, padding: '8px 12px', borderRadius: 8,
                    background: '#F0FBFF', border: '1px solid #7DD3FC', fontSize: 11,
                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                  }}>
                    <CheckCircle size={12} color="#2E8B98" />
                    <span style={{ fontWeight: 600, color: '#0C4A6E' }}>
                      Detected: {translateDetected.language_name}
                    </span>
                    {translateDetected.country_of_origin && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <MapPin size={10} color="#6C757D" />
                        {translateDetected.country_of_origin}
                      </span>
                    )}
                    {translateDetected.confidence && (
                      <Badge text={`${translateDetected.confidence}% confidence`} color="#62BB46" />
                    )}
                    {translateDetected.medical_domain_detected && (
                      <Badge text={translateDetected.medical_domain_detected} color="#4ECDC4" />
                    )}
                  </div>
                )}
              </div>

              {/* Source Text Input */}
              <div style={{ ...cardWrap, padding: '14px', marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>
                  Source Document (Original Language)
                </label>
                <textarea
                  value={translateInput}
                  onChange={e => setTranslateInput(e.target.value)}
                  placeholder="Paste the medical document text here in its original language. The AI will preserve all formatting — headings, tables, numbered lists, bullet points, section dividers, and indentation..."
                  style={{
                    ...inp, minHeight: 280, fontFamily: "'Courier New', monospace",
                    fontSize: 12, lineHeight: 1.6, resize: 'vertical', whiteSpace: 'pre-wrap',
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                  <span style={{ fontSize: 10, color: '#ADB5BD' }}>
                    {translateInput.length.toLocaleString()} characters
                  </span>
                  <button
                    onClick={handleTranslate}
                    disabled={translateLoading || !translateInput.trim()}
                    style={{ ...btnPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {translateLoading ? (
                      <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Translating...</>
                    ) : (
                      <><Globe size={14} /> Translate to English</>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Translated Output Panel */}
            {translateResult && (
              <div>
                {/* Translation Header */}
                <div style={{ ...cardWrap, padding: '14px', marginBottom: '12px', background: '#F0FDF4', border: '1px solid #86EFAC' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CheckCircle size={14} color="#16A34A" />
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>Translation Complete</span>
                    </div>
                    <button
                      onClick={handleCopyTranslation}
                      style={{ ...btnSm, display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                      {translateCopied ? <><Check size={12} color="#16A34A" /> Copied</> : <><Copy size={12} /> Copy</>}
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 11 }}>
                    <Badge text={`${translateResult.source_language_name} → ${translateResult.target_language_name}`} color="#2E8B98" />
                    {translateResult.confidence_score && (
                      <Badge text={`${translateResult.confidence_score}% confidence`} color="#62BB46" />
                    )}
                    {translateResult.source_country && translateResult.source_country !== 'Unknown' && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#374151', fontWeight: 500 }}>
                        <MapPin size={10} /> Research from: {translateResult.source_country}
                      </span>
                    )}
                    {translateResult.isAI && <Badge text="AI-Translated" color="#F26522" />}
                  </div>
                </div>

                {/* Translated Text */}
                <div style={{ ...cardWrap, padding: '14px', marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>
                    English Translation (Exact Format Preserved)
                  </label>
                  <div style={{
                    background: '#FAFAFA', padding: '14px', borderRadius: 8,
                    border: '1px solid #E5E7EB', fontFamily: "'Courier New', monospace",
                    fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                    maxHeight: 320, overflowY: 'auto',
                  }}>
                    {translateResult.translated_text}
                  </div>
                </div>

                {/* Country & Regulatory Notes */}
                {translateResult.regulatory_notes && (
                  <div style={{
                    ...cardWrap, padding: '12px 14px', marginBottom: '12px',
                    background: '#FFFBEB', border: '1px solid #FCD34D',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <AlertTriangle size={12} color="#D97706" />
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#92400E' }}>Country-Specific Notes</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 11, color: '#78350F', lineHeight: 1.5 }}>
                      {translateResult.regulatory_notes}
                    </p>
                  </div>
                )}

                {/* Preserved Medical Terms */}
                {(translateResult.medical_terms_preserved || []).length > 0 && (
                  <div style={{ ...cardWrap, padding: '12px 14px', marginBottom: '12px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>
                      Preserved Medical Terms ({translateResult.medical_terms_preserved.length})
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {translateResult.medical_terms_preserved.map((t, i) => (
                        <span key={i} style={{
                          padding: '3px 10px', borderRadius: 12, background: '#EDE9FE',
                          fontSize: 10, color: '#5B21B6', fontWeight: 500,
                        }}>{t}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Terminology Notes */}
                {(translateResult.terminology_notes || []).length > 0 && (
                  <div style={{ ...cardWrap, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>
                      Terminology Decisions
                    </div>
                    <div style={{ display: 'grid', gap: 6 }}>
                      {translateResult.terminology_notes.slice(0, 8).map((tn, i) => (
                        <div key={i} style={{
                          background: '#F9FAFB', padding: '8px 10px', borderRadius: 6,
                          borderLeft: '3px solid #2E8B98', fontSize: 11,
                        }}>
                          <span style={{ fontWeight: 600, color: '#212529' }}>{tn.original}</span>
                          <span style={{ color: '#6C757D' }}> → </span>
                          <span style={{ fontWeight: 600, color: '#2E8B98' }}>{tn.translated}</span>
                          {tn.note && <div style={{ color: '#6C757D', marginTop: 2 }}>{tn.note}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Translation History */}
          {translateHistory.length > 0 && (
            <div style={{ ...cardWrap, padding: '16px', marginTop: '16px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 13, fontWeight: 700 }}>Recent Translations</h3>
              <div style={{ display: 'grid', gap: 8 }}>
                {translateHistory.slice(0, 10).map(t => (
                  <div key={t.id} style={{
                    background: '#F9FAFB', padding: '10px 14px', borderRadius: 10,
                    border: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', cursor: 'pointer',
                  }}
                  onClick={() => {
                    setTranslateInput(t.source_text || '');
                    setTranslateResult({
                      translated_text: t.translated_text,
                      source_language: t.source_language,
                      source_language_name: t.source_language_name,
                      target_language_name: t.target_language_name,
                      confidence_score: t.confidence_score,
                      medical_terms_preserved: t.preserved_terms ? (typeof t.preserved_terms === 'string' ? JSON.parse(t.preserved_terms) : t.preserved_terms) : [],
                      terminology_notes: t.terminology_notes ? (typeof t.terminology_notes === 'string' ? JSON.parse(t.terminology_notes) : t.terminology_notes) : [],
                      source_country: t.center_country || 'Unknown',
                      isAI: true,
                    });
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>
                          {t.document_title || 'Untitled'}
                        </span>
                        <Badge text={t.document_type?.replace(/_/g, ' ')} color="#4ECDC4" />
                        <Badge text={`${t.source_language_name} → EN`} color="#2E8B98" />
                      </div>
                      <div style={{ fontSize: 11, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {t.center_country && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <MapPin size={10} /> {t.center_country}
                          </span>
                        )}
                        {t.center_name && <span>{t.center_name}</span>}
                        <span>{new Date(t.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {t.confidence_score && <Badge text={`${t.confidence_score}%`} color="#62BB46" />}
                      <Badge text={t.review_status} color={t.review_status === 'approved' ? '#62BB46' : '#F26522'} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ PROTOCOL LIBRARY VIEW ═══ */}
      {tab === 'library' && !selectedProtocol && (
        <div>
          {/* Filters */}
          <div style={{ ...cardWrap, padding: '14px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
              <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#ADB5BD' }} />
                <input
                  value={protocolSearch}
                  onChange={e => setProtocolSearch(e.target.value)}
                  placeholder="Search protocols..."
                  style={{ ...inp, paddingLeft: 32 }}
                />
              </div>
              <select value={therapyTypeFilter} onChange={e => setTherapyTypeFilter(e.target.value)} style={{ ...inp, width: 'auto', minWidth: 120 }}>
                <option value="">All Therapies</option>
                {Object.keys(THERAPY_COLORS).map(t => (
                  <option key={t} value={t}>{t.replace(/_/g, ' ').toUpperCase()}</option>
                ))}
              </select>
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{ ...inp, width: 'auto', minWidth: 120 }}>
                <option value="">All Categories</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inp, width: 'auto', minWidth: 100 }}>
                <option value="">All Status</option>
                {Object.keys(STATUS_COLORS).map(s => (
                  <option key={s} value={s}>{s.replace(/_/g, ' ').toUpperCase()}</option>
                ))}
              </select>
              <button onClick={loadProtocols} style={btnSm}><RefreshCw size={13} /></button>
            </div>

            {/* Cell Types Filter Chips */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#6C757D', alignSelf: 'center' }}>Cell Types:</span>
              {CELL_TYPES.map(ct => (
                <button
                  key={ct}
                  onClick={() => toggleCellType(ct)}
                  style={{
                    padding: '5px 12px', borderRadius: 16, border: 'none',
                    background: cellTypesFilter.includes(ct) ? '#2E8B98' : '#F8F9FA',
                    color: cellTypesFilter.includes(ct) ? '#fff' : '#6C757D',
                    cursor: 'pointer', fontSize: 11, fontWeight: 500,
                    boxShadow: neuShadowSm, transition: 'all 0.15s',
                  }}>
                  {ct}
                </button>
              ))}
            </div>
          </div>

          {/* Protocols Grid */}
          {loading ? (
            <div style={emptyMsg}><Loader size={20} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} /> Loading protocols...</div>
          ) : protocols.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
              {protocols.map(p => (
                <ProtocolCard key={p.id} protocol={p} onSelect={() => setSelectedProtocol(p)} />
              ))}
            </div>
          ) : (
            <div style={{ ...cardWrap, ...emptyMsg }}>
              <BookOpen size={32} color="#ADB5BD" style={{ marginBottom: 8 }} />
              <div>No protocols found</div>
            </div>
          )}

          <div style={{ marginTop: '16px' }}>
            <button onClick={() => setShowNewProtocolModal(true)} style={btnPrimary}>
              <Plus size={14} /> New Protocol
            </button>
          </div>
        </div>
      )}

      {/* ═══ PROTOCOL DETAIL VIEW ═══ */}
      {tab === 'library' && selectedProtocol && (
        <ProtocolDetail protocol={selectedProtocol} onBack={() => setSelectedProtocol(null)} />
      )}

      {/* ═══ AI SUGGESTIONS VIEW ═══ */}
      {tab === 'suggestions' && (
        <div>
          {/* Suggestion Form */}
          <div style={{ ...cardWrap, padding: '20px', marginBottom: '16px', borderLeft: '4px solid #2E8B98' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={16} color="#F26522" /> AI Protocol Suggestion Engine
            </h3>
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>
                  Describe Patient Condition & Protocol Goals
                </label>
                <textarea
                  value={suggestionCondition}
                  onChange={e => setSuggestionCondition(e.target.value)}
                  placeholder="E.g., Patient with metastatic breast cancer, HER2-positive, exploring CAR-T or combination immunotherapy..."
                  style={{ ...inp, minHeight: 80, fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>
                    Diagnosis Codes (comma-separated)
                  </label>
                  <input
                    value={suggestionDiagnosisCodes}
                    onChange={e => setSuggestionDiagnosisCodes(e.target.value)}
                    placeholder="ICD-10 codes, e.g., C50.911, C80.1"
                    style={inp}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>
                    Therapy Preference (optional)
                  </label>
                  <select value={suggestionTherapyType} onChange={e => setSuggestionTherapyType(e.target.value)} style={inp}>
                    <option value="">Any therapy type</option>
                    {Object.keys(THERAPY_COLORS).map(t => (
                      <option key={t} value={t}>{t.replace(/_/g, ' ').toUpperCase()}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                onClick={handleRequestAISuggestion}
                disabled={suggestionLoading}
                style={{ ...btnPrimary, alignSelf: 'flex-start' }}>
                {suggestionLoading ? (
                  <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Getting suggestions...</>
                ) : (
                  <><Send size={14} /> Get AI Suggestions</>
                )}
              </button>
            </div>
          </div>

          {/* AI Results */}
          {suggestionResults && (
            <div style={{
              ...cardWrap, padding: '20px', marginBottom: '16px',
              background: '#F0FBFF',
              borderLeft: '4px solid #F26522',
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 700 }}>AI Suggestions</h3>

              {/* Existing Protocol Matches */}
              {suggestionResults.existing_matches && suggestionResults.existing_matches.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: 12, fontWeight: 600, color: '#2E8B98' }}>Existing Protocol Matches</h4>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {suggestionResults.existing_matches.map(m => (
                      <div key={m.id} style={{
                        background: '#fff', padding: '12px 14px', borderRadius: 10,
                        border: '1px solid #E5E7EB',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>{m.protocol_title}</span>
                          <Badge text={`${m.match_confidence}% match`} color="#2E8B98" />
                        </div>
                        <p style={{ fontSize: 12, color: '#6C757D', margin: 0 }}>{m.rationale}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Novel Hypothesis */}
              {suggestionResults.novel_hypothesis && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(242, 101, 34, 0.05) 0%, rgba(46, 139, 152, 0.05) 100%)',
                  border: '2px solid #F26522',
                  borderRadius: 10, padding: '14px 16px', marginBottom: 16,
                }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: 12, fontWeight: 700, color: '#F26522', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Zap size={14} /> AI-Generated Novel Protocol Hypothesis
                  </h4>
                  <p style={{ fontSize: 12, color: '#212529', margin: '0 0 8px 0', lineHeight: 1.6 }}>
                    {suggestionResults.novel_hypothesis.description}
                  </p>
                  {suggestionResults.novel_hypothesis.rationale && (
                    <p style={{ fontSize: 11, color: '#6C757D', margin: 0 }}>
                      <strong>Rationale:</strong> {suggestionResults.novel_hypothesis.rationale}
                    </p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button style={{ ...btnSm, background: '#62BB46', color: '#fff' }}>
                  <CheckCircle size={12} /> Accept
                </button>
                <button style={{ ...btnSm }}>
                  <Eye size={12} /> Review
                </button>
                <button style={{ ...btnSm, background: '#DC3545', color: '#fff' }}>
                  <XCircle size={12} /> Reject
                </button>
              </div>
            </div>
          )}

          {/* Saved Suggestions List */}
          {!suggestionResults && (
            <div style={{ ...cardWrap, ...emptyMsg }}>
              <Sparkles size={32} color="#ADB5BD" style={{ marginBottom: 8 }} />
              <div>Enter a patient condition above to generate AI-powered protocol suggestions</div>
            </div>
          )}
        </div>
      )}

      {/* ═══ CATEGORIES VIEW ═══ */}
      {tab === 'categories' && (
        <div>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 14, fontWeight: 700 }}>Protocol Categories</h3>
          {categories.length > 0 ? (
            <div style={{ display: 'grid', gap: 8 }}>
              {categories.map(cat => (
                <CategoryTreeNode
                  key={cat.id}
                  category={cat}
                  expanded={expandedCategory === cat.id}
                  onToggle={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                  onSelect={() => {
                    setCategoryFilter(cat.id);
                    setTab('library');
                  }}
                />
              ))}
            </div>
          ) : (
            <div style={{ ...cardWrap, ...emptyMsg }}>
              <Layers size={32} color="#ADB5BD" style={{ marginBottom: 8 }} />
              <div>No categories available</div>
            </div>
          )}
        </div>
      )}

      {/* ═══ MODALS ═══ */}
      {showNewProtocolModal && (
        <NewProtocolModal
          onClose={() => setShowNewProtocolModal(false)}
          onSave={handleCreateProtocol}
          categories={categories}
        />
      )}
    </div>
  );
}

// ─── ProtocolCard Component ───────────────────────────────────────────────

function ProtocolCard({ protocol, onSelect }) {
  const therapyColor = THERAPY_COLORS[protocol.therapy_type] || '#6C757D';
  const statusColor = STATUS_COLORS[protocol.status] || '#6C757D';

  return (
    <div
      onClick={onSelect}
      style={{
        ...cardWrap, padding: '16px', cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '8px 8px 16px rgba(186,199,216,0.55), -8px -8px 16px rgba(255,255,255,0.9)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = neuShadow;
      }}>
      {/* Header */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6C757D' }}>{protocol.protocol_code}</div>
          <Badge text={protocol.status} color={statusColor} />
        </div>
        <h4 style={{ margin: '0 0 4px 0', fontSize: 13, fontWeight: 700, color: '#212529' }}>{protocol.title}</h4>
        <p style={{ margin: 0, fontSize: 11, color: '#6C757D' }}>{protocol.condition_treated}</p>
      </div>

      {/* Badges */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        <Badge text={protocol.therapy_type} color={therapyColor} />
        {protocol.cell_types && protocol.cell_types.slice(0, 2).map(ct => (
          <Badge key={ct} text={ct} color="#4ECDC4" />
        ))}
      </div>

      {/* Efficacy Score Bar */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: '#6C757D', fontWeight: 500 }}>Efficacy Score</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#212529' }}>{protocol.efficacy_score || 'N/A'}</span>
        </div>
        <div style={{ background: '#E5E7EB', borderRadius: 6, overflow: 'hidden', height: 6 }}>
          <div style={{
            background: '#62BB46',
            height: '100%',
            width: `${Math.min((protocol.efficacy_score || 0) * 10, 100)}%`,
          }} />
        </div>
      </div>

      {/* Click CTA */}
      <div style={{ display: 'flex', alignItems: 'center', color: '#2E8B98', fontSize: 12, fontWeight: 600 }}>
        View Details <ChevronRight size={12} style={{ marginLeft: 4 }} />
      </div>
    </div>
  );
}

// ─── SuggestionCard Component ────────────────────────────────────────────

function SuggestionCard({ suggestion }) {
  return (
    <div style={{
      background: '#F0FBFF', padding: '12px 14px', borderRadius: 10,
      border: '1px solid #7DD3FC',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{suggestion.condition}</span>
        <Badge text={suggestion.status || 'pending'} color="#2E8B98" />
      </div>
      <p style={{ margin: 0, fontSize: 11, color: '#6C757D' }}>
        Suggested: {suggestion.suggested_protocol_title || 'Protocol analysis pending'}
      </p>
    </div>
  );
}

// ─── AnalysisCard Component ─────────────────────────────────────────────

function AnalysisCard({ analysis }) {
  return (
    <div style={{
      background: '#F9FAFB', padding: '12px 14px', borderRadius: 10,
      border: '1px solid #E5E7EB',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{analysis.analysis_type}</span>
        <span style={{ fontSize: 10, color: '#ADB5BD' }}>{new Date(analysis.created_at).toLocaleDateString()}</span>
      </div>
      <p style={{ margin: 0, fontSize: 11, color: '#6C757D' }}>
        Protocol: {analysis.protocol_code}
      </p>
    </div>
  );
}

// ─── CategoryTreeNode Component ──────────────────────────────────────────

function CategoryTreeNode({ category, expanded, onToggle, onSelect }) {
  return (
    <div>
      <div
        onClick={onToggle}
        style={{
          ...cardWrap, padding: '12px 14px', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          {(category.subcategories || []).length > 0 ? (
            expanded ? <ChevronDown size={14} color="#2E8B98" /> : <ChevronRight size={14} color="#6C757D" />
          ) : (
            <div style={{ width: 14 }} />
          )}
          <span style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{category.name}</span>
        </div>
        <span style={{
          fontSize: 11, color: '#ADB5BD', background: '#F3F4F6',
          padding: '2px 8px', borderRadius: 6, marginLeft: 8,
        }}>
          {category.protocol_count || 0}
        </span>
      </div>

      {/* Subcategories */}
      {expanded && (category.subcategories || []).length > 0 && (
        <div style={{ paddingLeft: '16px', marginTop: 8, display: 'grid', gap: 6 }}>
          {category.subcategories.map(sub => (
            <div
              key={sub.id}
              onClick={() => onSelect()}
              style={{
                ...cardWrap, padding: '10px 12px', cursor: 'pointer',
                fontSize: 12, color: '#374151',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
              <span>{sub.name}</span>
              <span style={{ fontSize: 10, color: '#ADB5BD' }}>{sub.protocol_count || 0}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ProtocolDetail Component ────────────────────────────────────────────

function ProtocolDetail({ protocol, onBack }) {
  const [components, setComponents] = useState([]);
  const [results, setResults] = useState([]);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDetails = async () => {
      setLoading(true);
      try {
        const [comps, res, hist] = await Promise.all([
          api()?.getMIComponents?.(protocol.id).catch(() => []),
          api()?.getMIResults?.(protocol.id).catch(() => []),
          api()?.getMIAnalysisHistory?.(protocol.id).catch(() => []),
        ]);
        if (comps) setComponents(Array.isArray(comps) ? comps : []);
        if (res) setResults(Array.isArray(res) ? res : []);
        if (hist) setAnalysisHistory(Array.isArray(hist) ? hist : []);
      } catch (err) {
        console.error('[MI] Detail load error:', err);
      }
      setLoading(false);
    };
    loadDetails();
  }, [protocol.id]);

  const therapyColor = THERAPY_COLORS[protocol.therapy_type] || '#6C757D';
  const statusColor = STATUS_COLORS[protocol.status] || '#6C757D';

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        style={{
          ...btnSm, marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
        <ArrowLeft size={12} /> Back to Library
      </button>

      {/* Header */}
      <div style={{ ...cardWrap, padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#212529' }}>{protocol.title}</h2>
            <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6C757D' }}>Code: {protocol.protocol_code}</p>
          </div>
          <Badge text={protocol.status} color={statusColor} />
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <Badge text={protocol.therapy_type} color={therapyColor} />
          {protocol.cell_types && protocol.cell_types.map(ct => (
            <Badge key={ct} text={ct} color="#4ECDC4" />
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
          <div>
            <div style={{ fontSize: 10, color: '#ADB5BD', fontWeight: 600 }}>CONDITION</div>
            <div style={{ fontSize: 12, color: '#212529', fontWeight: 600 }}>{protocol.condition_treated}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#ADB5BD', fontWeight: 600 }}>EFFICACY SCORE</div>
            <div style={{ fontSize: 12, color: '#212529', fontWeight: 600 }}>{protocol.efficacy_score || 'N/A'}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#ADB5BD', fontWeight: 600 }}>SAFETY SCORE</div>
            <div style={{ fontSize: 12, color: '#212529', fontWeight: 600 }}>{protocol.safety_score || 'N/A'}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={emptyMsg}><Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading details...</div>
      ) : (
        <>
          {/* Components */}
          <div style={{ ...cardWrap, padding: '16px', marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 13, fontWeight: 700 }}>Protocol Components</h3>
            {components.length > 0 ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {components.map(comp => (
                  <div key={comp.id} style={{
                    background: '#F9FAFB', padding: '12px 14px', borderRadius: 10,
                    borderLeft: '3px solid #2E8B98',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{comp.name}</span>
                      <Badge text={comp.type} color="#4ECDC4" />
                    </div>
                    <p style={{ margin: 0, fontSize: 11, color: '#6C757D' }}>
                      Dosage: {comp.dosage} | Frequency: {comp.frequency} | Duration: {comp.duration}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ ...emptyMsg, padding: 12, fontSize: 11 }}>No components defined</div>
            )}
          </div>

          {/* Results/Outcomes */}
          <div style={{ ...cardWrap, padding: '16px', marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 13, fontWeight: 700 }}>Results & Outcomes</h3>
            {results.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                {results.map(res => (
                  <div key={res.id} style={{
                    background: '#F9FAFB', padding: '12px 14px', borderRadius: 10,
                    border: '1px solid #E5E7EB',
                  }}>
                    <div style={{ fontSize: 11, color: '#6C757D', marginBottom: 4 }}>{res.metric_name}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#212529' }}>{res.metric_value}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ ...emptyMsg, padding: 12, fontSize: 11 }}>No results recorded</div>
            )}
          </div>

          {/* Analysis History */}
          <div style={{ ...cardWrap, padding: '16px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 13, fontWeight: 700 }}>AI Analysis History</h3>
            {analysisHistory.length > 0 ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {analysisHistory.slice(0, 5).map(a => (
                  <div key={a.id} style={{
                    background: '#F9FAFB', padding: '10px 12px', borderRadius: 8,
                    fontSize: 11, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <span>{a.analysis_type}</span>
                    <span style={{ color: '#ADB5BD', fontSize: 10 }}>{new Date(a.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ ...emptyMsg, padding: 12, fontSize: 11 }}>No analyses yet</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── New Protocol Modal ───────────────────────────────────────────────────

function NewProtocolModal({ onClose, onSave, categories }) {
  const [form, setForm] = useState({
    title: '', description: '', therapy_type: '', condition_treated: '',
    cell_types: [], components: [],
  });
  const [componentForm, setComponentForm] = useState({ type: '', name: '', dosage: '', frequency: '', duration: '' });
  const [saving, setSaving] = useState(false);

  const toggleCellType = (ct) => {
    if (form.cell_types.includes(ct)) {
      setForm({ ...form, cell_types: form.cell_types.filter(t => t !== ct) });
    } else {
      setForm({ ...form, cell_types: [...form.cell_types, ct] });
    }
  };

  const addComponent = () => {
    if (!componentForm.name.trim()) return;
    setForm({
      ...form,
      components: [...form.components, { ...componentForm, id: Math.random() }],
    });
    setComponentForm({ type: '', name: '', dosage: '', frequency: '', duration: '' });
  };

  const removeComponent = (id) => {
    setForm({ ...form, components: form.components.filter(c => c.id !== id) });
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.therapy_type) {
      alert('Title and therapy type required');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 640, maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>New Protocol</h2>
          <button onClick={onClose} style={closeBtn}><X size={20} /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>Title *</label>
            <input
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Protocol title"
              style={inp}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>Therapy Type *</label>
            <select value={form.therapy_type} onChange={e => setForm({ ...form, therapy_type: e.target.value })} style={inp}>
              <option value="">Select...</option>
              {Object.keys(THERAPY_COLORS).map(t => (
                <option key={t} value={t}>{t.replace(/_/g, ' ').toUpperCase()}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>Condition Treated</label>
            <input
              value={form.condition_treated}
              onChange={e => setForm({ ...form, condition_treated: e.target.value })}
              placeholder="Medical condition"
              style={inp}
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Protocol details..."
              style={{ ...inp, minHeight: 60, fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>Cell Types</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {CELL_TYPES.map(ct => (
                <button
                  key={ct}
                  onClick={() => toggleCellType(ct)}
                  type="button"
                  style={{
                    padding: '5px 12px', borderRadius: 16, border: 'none',
                    background: form.cell_types.includes(ct) ? '#2E8B98' : '#F8F9FA',
                    color: form.cell_types.includes(ct) ? '#fff' : '#6C757D',
                    cursor: 'pointer', fontSize: 11, fontWeight: 500,
                    boxShadow: neuShadowSm,
                  }}>
                  {ct}
                </button>
              ))}
            </div>
          </div>

          {/* Components */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>Components</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 1fr 1fr auto', gap: 6, alignItems: 'end', marginBottom: 12 }}>
              <input value={componentForm.type} onChange={e => setComponentForm({ ...componentForm, type: e.target.value })} placeholder="Type" style={{ ...inp, width: 60 }} />
              <input value={componentForm.name} onChange={e => setComponentForm({ ...componentForm, name: e.target.value })} placeholder="Name" style={inp} />
              <input value={componentForm.dosage} onChange={e => setComponentForm({ ...componentForm, dosage: e.target.value })} placeholder="Dosage" style={inp} />
              <input value={componentForm.frequency} onChange={e => setComponentForm({ ...componentForm, frequency: e.target.value })} placeholder="Frequency" style={inp} />
              <input value={componentForm.duration} onChange={e => setComponentForm({ ...componentForm, duration: e.target.value })} placeholder="Duration" style={inp} />
              <button onClick={addComponent} type="button" style={{ ...btnSm }}><Plus size={12} /></button>
            </div>

            {form.components.length > 0 && (
              <div style={{ display: 'grid', gap: 6 }}>
                {form.components.map(comp => (
                  <div key={comp.id} style={{
                    background: '#F9FAFB', padding: '8px 12px', borderRadius: 8,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11,
                  }}>
                    <span>{comp.name} - {comp.dosage}</span>
                    <button onClick={() => removeComponent(comp.id)} type="button" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                      <X size={12} color="#DC3545" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSm}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>
            {saving ? 'Saving...' : 'Create Protocol'}
          </button>
        </div>
      </div>
    </div>
  );
}
